import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translate } from './messages';

export const LANGUAGE_KEY = 'antsmeeting.ui-language';
export const languages = Object.freeze([['ko', '한국어'], ['en', 'English'], ['zh', '中文(简体)'], ['ja', '日本語']]);
const supported = language => languages.some(([code]) => code === language);
export const normalizeLanguage = value => {
  if (typeof value !== 'string') return null;
  const code = value.trim().toLowerCase().split(/[-_]/)[0];
  return supported(code) ? code : null;
};

export function initialLanguage() {
  const native = normalizeLanguage(window.__ANTSMEETING_ENTRY__?.language);
  if (native) return native;
  try {
    const saved = normalizeLanguage(window.localStorage.getItem(LANGUAGE_KEY));
    if (saved) return saved;
  } catch (_) { /* blocked storage must not prevent rendering */ }
  if (typeof navigator !== 'undefined') {
    for (const language of navigator.languages?.length ? navigator.languages : [navigator.language]) {
      const code = normalizeLanguage(language);
      if (code) return code;
    }
  }
  return 'en';
}

const LocaleContext = createContext(null);
export function LocaleProvider({ children }) {
  const [language, updateLanguage] = useState(initialLanguage);
  const [saveError, setSaveError] = useState(false);
  const setLanguage = useCallback(next => {
    if (!supported(next)) return false;
    try { window.localStorage.setItem(LANGUAGE_KEY, next); }
    catch (_) { setSaveError(true); return false; }
    updateLanguage(next);
    setSaveError(false);
    return true;
  }, []);
  useEffect(() => {
    const previous = document.documentElement.lang;
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : language;
    return () => { document.documentElement.lang = previous; };
  }, [language]);
  useEffect(() => {
    const receive = event => {
      let storage;
      try { storage = window.localStorage; } catch (_) { return; }
      if (event.key !== LANGUAGE_KEY || event.storageArea !== storage) return;
      const next = normalizeLanguage(event.newValue);
      if (next) { updateLanguage(next); setSaveError(false); }
    };
    window.addEventListener('storage', receive);
    return () => window.removeEventListener('storage', receive);
  }, []);
  const t = useCallback(key => translate(language, key), [language]);
  const value = useMemo(() => ({ language, setLanguage, t, saveError }), [language, setLanguage, t, saveError]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('LocaleProvider is required');
  return value;
}

export function LanguageSelect() {
  const { language, setLanguage, t, saveError } = useLocale();
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', maxWidth: '100%' }}>
    <select aria-label={t('언어')} value={language} onChange={event => setLanguage(event.target.value)} style={{ font: 'inherit', maxWidth: '100%', padding: '7px 4px' }}>
      {languages.map(([code, label]) => <option key={code} value={code} lang={code === 'zh' ? 'zh-CN' : code}>{label}</option>)}
    </select>
    {saveError && <span role="alert">{t('언어 설정을 저장하지 못했습니다.')}</span>}
  </div>;
}
